import { SyntheticEvent, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import Tab from "@mui/material/Tab";

import {
    AutoSyncIcon,
    colors,
    HistoryIcon,
    InfoIcon,
    TaskGropsIcon
} from "@dotnet/telematics-design-system";

import { BUTTON_SIZES } from "components/DesignComponents/Button/types/enums";
import CopyButton from "components/DesignComponents/CopyButton";
import SegmentedToggle, {
    TAB_SIZES
} from "components/DesignComponents/SegmentedToggle";
import TableContext from "context/table/tableContext";
import { INVALID } from "shared/constants/strings";
import { getEnvironment } from "shared/functions";
import { useMediaQueries } from "shared/hooks/useMediaQueries";

import Actions from "./Actions";
import Changes from "./Changes";
import Configuration from "./Configuration";
import { LABELS } from "./constants";
import Details, { isFtPlatform } from "./Details";
import ModelImage from "./ModelImage";
import TaskQueue from "./TaskQueue";
import { MAIN_TABS } from "./types/enums";

const DeviceDetails = () => {
    const { gray700, gray900, textPlaceholder } = colors;

    const { detailsData } = useContext(TableContext);

    const { t } = useTranslation();
    const { toMd } = useMediaQueries();

    const { isProductionEnv } = getEnvironment();

    const [tabValue, setTabValue] = useState(MAIN_TABS.Details);

    const { imei, model, model_platform } = detailsData;

    // Set the "Device details" tab when the current tab is "Configuration" and the selected device is not an FT platfom device
    useEffect(() => {
        if (
            tabValue === MAIN_TABS.Configuration &&
            !isFtPlatform(model_platform)
        ) {
            setTabValue(MAIN_TABS.Details);
        }
    }, [tabValue, model_platform]);

    const handleTabChange = (_: SyntheticEvent, newValue: MAIN_TABS) =>
        setTabValue(newValue);

    const modelName = () => {
        if (model) {
            return model === INVALID ? t(`General##${INVALID}`) : model;
        }

        return "-";
    };

    const TABS = [
        { label: LABELS.DeviceDetails, icon: <InfoIcon /> },
        { label: LABELS.Tasks, icon: <TaskGropsIcon /> },
        { label: LABELS.Changes, icon: <HistoryIcon /> },
        ...(!isProductionEnv && isFtPlatform(model_platform)
            ? [{ label: LABELS.Configuration, icon: <AutoSyncIcon /> }]
            : [])
    ];

    return (
        <>
            <div
                css={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "16px",

                    "@media(max-width: 839px)": {
                        flexDirection: "column",
                        alignItems: "flex-start"
                    }
                })}
            >
                <div
                    css={css({
                        display: "flex",
                        alignItems: "center",

                        "@media(max-width: 599px)": {
                            flexDirection: "column",
                            alignItems: "flex-start"
                        }
                    })}
                >
                    <div
                        css={css({
                            width: "80px",
                            height: "56px",
                            borderRadius: "6px",
                            marginRight: "8px",

                            img: {
                                maxwidth: "100%",
                                maxHeight: "100%",
                                borderRadius: "6px"
                            },

                            "@media(max-width: 599px)": {
                                marginRight: "0"
                            }
                        })}
                    >
                        <ModelImage model={model} />
                    </div>

                    <div>
                        <div
                            css={css({
                                fontSize: "24px",
                                fontWeight: "500",
                                lineHeight: "32px",
                                textTransform: "uppercase",

                                "span:first-of-type": {
                                    color: textPlaceholder
                                },

                                "span:last-of-type": {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    color: gray900,

                                    "& > button": {
                                        marginLeft: "8px"
                                    }
                                }
                            })}
                        >
                            <span>{t("Table##imei")}:</span>{" "}
                            <span>
                                {imei}

                                <CopyButton
                                    size={BUTTON_SIZES.Tiny}
                                    copiedValue={imei}
                                />
                            </span>
                        </div>

                        <div
                            css={css({
                                display: "flex",
                                alignItems: "center",
                                columnGap: "16px",
                                fontSize: "14px",
                                fontWeight: "500",
                                lineHeight: "20px",
                                letterSpacing: "0.1px",
                                marginTop: "4px",

                                "span:first-of-type": {
                                    color: textPlaceholder
                                },

                                "span:last-of-type": {
                                    color: gray700
                                }
                            })}
                        >
                            <div>
                                <span>{t("Table##model")}:</span>{" "}
                                <span>{modelName()}</span>
                            </div>

                            <div>
                                <span>{t("Table##platform")}:</span>{" "}
                                <span>
                                    {model_platform
                                        ? t(`General##${model_platform}`)
                                        : "-"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <Actions />
            </div>

            <div>
                <SegmentedToggle
                    value={tabValue}
                    size={TAB_SIZES.Small}
                    tabWidth={toMd ? "258px" : "250px"}
                    onChange={handleTabChange}
                >
                    {TABS.map(({ icon, label }) => (
                        <Tab
                            key={label}
                            label={t(`Table##${label}`)}
                            icon={icon}
                        />
                    ))}
                </SegmentedToggle>
            </div>

            {tabValue === MAIN_TABS.Details && (
                <Details key={imei} imei={imei} />
            )}

            {tabValue === MAIN_TABS.Tasks && <TaskQueue />}
            {tabValue === MAIN_TABS.Changes && <Changes />}

            {tabValue === MAIN_TABS.Configuration &&
                isFtPlatform(model_platform) && <Configuration />}
        </>
    );
};

export default DeviceDetails;
